/**
 * Click-through unificado de SuCom (`/go/<ticket>`).
 *
 * Un único endpoint registra el click y decide el destino:
 *  - Tienda CON affiliate network + deeplink → aplica el deeplink de la red
 *    (Admitad, Soicos, …) con el subid de SuCom como tracking.
 *  - Tienda SIN network → va directo a la URL con utm_source=sucom.
 *
 * El "ticket" del path es un blob base64url isomórfico (se arma en el cliente
 * sin tocar BD) con el contexto del click. En el hit, el server genera el
 * clickId/subid real (que SÍ contiene userId, network, storeId…).
 */

export type GoTab = "producto" | "comparacion" | "cashback" | "coupon" | "tienda";

/** Contexto del click embebido en el path `/go/<ticket>`. */
export interface GoTicket {
  /** SuCom storeId. */ s: string;
  /** URL destino (producto/oferta). */ u?: string;
  /** productId. */ p?: string;
  /** couponId. */ c?: string;
  /** cashbackId / cashback transaction. */ cb?: string;
  /** Pestaña/origen del click. */ t?: GoTab;
}

function b64urlEncode(s: string): string {
  const b64 =
    typeof btoa !== "undefined"
      ? btoa(unescape(encodeURIComponent(s)))
      : Buffer.from(s, "utf8").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64urlDecode(s: string): string {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  return typeof atob !== "undefined"
    ? decodeURIComponent(escape(atob(b64)))
    : Buffer.from(b64, "base64").toString("utf8");
}

export function encodeGoTicket(t: GoTicket): string {
  const compact: GoTicket = { s: t.s };
  if (t.u) compact.u = t.u;
  if (t.p) compact.p = t.p;
  if (t.c) compact.c = t.c;
  if (t.cb) compact.cb = t.cb;
  if (t.t) compact.t = t.t;
  return b64urlEncode(JSON.stringify(compact));
}

export function decodeGoTicket(s: string): GoTicket | null {
  try {
    const o = JSON.parse(b64urlDecode(s)) as GoTicket;
    return o && typeof o.s === "string" && o.s ? o : null;
  } catch {
    return null;
  }
}

/** Href del click-through para usar en cualquier `<a>` de salida a tienda. */
export function goUrl(t: GoTicket): string {
  return `/go/${encodeGoTicket(t)}`;
}

/**
 * Determina si una URL de producto de Grupo La Comer tiene la forma que
 * entiende el router Angular de la tienda. Se aceptan tanto las rutas que
 * genera el listado como el formato directo documentado por La Comer; lo que
 * sí invalida el enlace es que falte la ruta `detarticulo` o la sucursal.
 */
export function isCanonicalLaComerProductUrl(rawUrl: string | null | undefined): boolean {
  if (!rawUrl) return false;
  try {
    const url = new URL(rawUrl);
    if (!/(?:^|\.)lacomer\.com\.mx$/i.test(url.hostname)) return false;
    if (url.pathname.replace(/\/$/, "") !== "/lacomer") return false;
    return Boolean(parseLaComerDetail(url));
  } catch {
    return false;
  }
}

const LA_COMER_DETAIL_RE =
  /^!\/detarticulo\/([^/?#]+)\/(\d+)\/(\d+)\/(\d+)\/([^/?#]*)\/([^/?#]*)\/(\d+)(?:\/(\d+))?\?([^#]*)$/i;

/**
 * Parses a La Comer hash-route and verifies the branch parameters required by
 * the store. Keeping this in one place avoids treating an old Angular list
 * route as a different product from the direct detail route.
 */
function parseLaComerDetail(url: URL): { ean: string; params: URLSearchParams } | null {
  const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  const match = hash.match(LA_COMER_DETAIL_RE);
  if (!match) return null;
  const [, ean, , , , , , , , query] = match;
  if (!ean) return null;
  const params = new URLSearchParams(query);
  if (!/^\d+$/.test(params.get("succId") ?? "") || !/^\d+$/.test(params.get("succFmt") ?? "")) {
    return null;
  }
  return { ean, params };
}

/**
 * Converts any valid La Comer detail URL to the official direct-detail shape:
 * `#!/detarticulo/EAN/0/0/0///0?succId=...&succFmt=...`.
 *
 * Older catalog links used list/aisle segments. They can be valid Angular
 * states in some sessions but return 404 in others; normalizing them at the
 * `/go` boundary makes every existing ClickLink use the stable route. UTM
 * parameters that were accidentally embedded in the hash are moved before
 * the hash as part of the same repair.
 */
export function normalizeLaComerProductUrl(rawUrl: string | null | undefined): string | null {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl);
    if (!/(?:^|\.)lacomer\.com\.mx$/i.test(url.hostname)) return null;
    if (url.pathname.replace(/\/$/, "") !== "/lacomer") return null;
    const parsed = parseLaComerDetail(url);
    if (!parsed) return null;

    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
      const value = parsed.params.get(key);
      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
      parsed.params.delete(key);
    }

    const internalQuery = parsed.params.toString();
    url.hash = `#!/detarticulo/${parsed.ean}/0/0/0///0${internalQuery ? `?${internalQuery}` : ""}`;
    return url.toString();
  } catch {
    return null;
  }
}

/** El subid/ticket de SuCom que se envía a la red (clickId en BD). */
export interface SubidPayload {
  /** nonce único del click. */ ck: string;
  /** userId o id de invitado (cookie). */ u?: string | null;
  /** productId. */ p?: string | null;
  /** couponId. */ c?: string | null;
  /** cashbackId. */ cb?: string | null;
  /** tab. */ t?: GoTab | null;
  /** network (admitad/soicos/…). */ n?: string | null;
  /** SuCom storeId. */ s: string;
  /** storeId dentro de la red (short id). */ ns?: string | null;
}

export function encodeSubid(p: SubidPayload): string {
  const clean: Record<string, string> = { ck: p.ck, s: p.s };
  if (p.u) clean.u = p.u;
  if (p.p) clean.p = p.p;
  if (p.c) clean.c = p.c;
  if (p.cb) clean.cb = p.cb;
  if (p.t) clean.t = p.t;
  if (p.n) clean.n = p.n;
  if (p.ns) clean.ns = p.ns;
  return b64urlEncode(JSON.stringify(clean));
}

export function decodeSubid(s: string): SubidPayload | null {
  try {
    const o = JSON.parse(b64urlDecode(s)) as SubidPayload;
    return o && typeof o.ck === "string" ? o : null;
  } catch {
    return null;
  }
}

/** Añade utm_source=sucom (y campos de atribución) a la URL de una tienda directa. */
export function addUtm(rawUrl: string, opts: { campaign?: string | null; content?: string | null } = {}): string {
  try {
    const u = new URL(rawUrl);

    // En SPAs con hash routing (Grupo La Comer), la ruta del producto vive en
    // `#!...` y su propio query (`succId`/`succFmt`) debe quedarse dentro del
    // fragmento. Los UTM, en cambio, son parámetros de campaña de la URL y
    // deben ir en el query normal, antes del `#`. Si se agregan después de
    // `succFmt` el router Angular puede interpretar `utm_*` como parte de sus
    // parámetros de producto y responder 404. Este caso también normaliza
    // enlaces heredados que ya traían UTM dentro del fragmento.
    if (/^#!?\//.test(u.hash)) {
      const fragment = u.hash.slice(1);
      const question = fragment.indexOf("?");
      const route = question >= 0 ? fragment.slice(0, question) : fragment;
      const params = new URLSearchParams(question >= 0 ? fragment.slice(question + 1) : "");
      // Mueve UTM heredados de enlaces generados por versiones anteriores al
      // query normal para que nunca se queden dentro del estado hash.
      for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
        const value = params.get(key);
        if (value && !u.searchParams.has(key)) u.searchParams.set(key, value);
        params.delete(key);
      }
      // Las UTM se agregan antes del hash; la consulta interna conserva sólo
      // los parámetros que entiende el sitio (por ejemplo succId/succFmt).
      addTrackingParams(u.searchParams, opts);
      u.hash = params.toString() ? `${route}?${params.toString()}` : route;
    } else {
      addTrackingParams(u.searchParams, opts);
    }
    return u.toString();
  } catch {
    return rawUrl;
  }
}

function addTrackingParams(
  params: URLSearchParams,
  opts: { campaign?: string | null; content?: string | null },
) {
  if (!params.has("utm_source")) params.set("utm_source", "sucom");
  if (!params.has("utm_medium")) params.set("utm_medium", "comparador");
  if (opts.campaign && !params.has("utm_campaign")) params.set("utm_campaign", opts.campaign);
  if (opts.content && !params.has("utm_content")) params.set("utm_content", opts.content);
}
